import { formatDateWithTimeZone } from "@cp/toolkit";
import type { DraftMessageFragment } from "@qw/graphql";
import {
  Badge,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@qw/ui";
import { type FC, useCallback } from "react";
import { ConversationLinkButton, ConversationLinkCheckbox, ConversationLinkContent } from "@/inbox/conversation-link";
import { useDeleteDraft } from "@/providers/use-delete-draft";
import { useDraftsCompose } from "@/providers/use-drafts-compose";

export interface DraftLinkProps {
  draft: DraftMessageFragment;
}

export const DraftLink: FC<DraftLinkProps> = ({ draft }) => {
  const { openDraft } = useDraftsCompose();
  const [deleteDraft] = useDeleteDraft();

  const handleClick = useCallback(() => openDraft(draft.id), [openDraft, draft.id]);
  const handleDelete = useCallback(() => deleteDraft(draft.id), [deleteDraft, draft.id]);

  return (
    <div className="p-1">
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <ConversationLinkButton onClick={handleClick}>
            <ConversationLinkContent
              icon={<ConversationLinkCheckbox row={draft} />}
              from={draft.from}
              date={draft.date}
              subject={draft.subject ?? ""}
              badges={<DraftLinkBadge draft={draft} />}
            />
          </ConversationLinkButton>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={handleDelete}>
            <Icon icon="delete" />
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </div>
  );
};

const DraftLinkBadge: FC<{ draft: DraftMessageFragment }> = ({ draft }) => {
  switch (draft.scheduledSend?.status) {
    case "pending":
    case "queued":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="gap-1 bg-primary/10 text-primary">
              <Icon icon="schedule_send" />
              Scheduled
            </Badge>
          </TooltipTrigger>
          <TooltipContent hidden={!draft.scheduledSend?.sendAt}>
            {draft.scheduledSend?.sendAt &&
              formatDateWithTimeZone(new Date(draft.scheduledSend.sendAt), "EEE, LLL d, yyyy 'at' h:mm a")}
          </TooltipContent>
        </Tooltip>
      );
    case "failed":
      return (
        <Badge variant="destructive" className="gap-1 bg-destructive/10 text-destructive">
          <Icon icon="error" /> Failed
        </Badge>
      );
    default:
      return null;
  }
};
